import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/constants';
import { getCollectorReconciliation } from '@/services/ops.service';
import { useFSAReconciliation } from '@/features/reconciliation/hooks/useFSAReconciliation';
import { usePCFReconciliation } from '@/features/reconciliation/hooks/usePCFReconciliation';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Operaciones', path: ROUTES.RECONCILIATION_DASHBOARD },
  { label: 'Conciliacion', path: ROUTES.RECONCILIATION_DASHBOARD },
];

// ─── Block colors ───────────────────────────────────────────────────────────

const CLR = {
  fi: '#ec4899',     // pink
  col: '#f59e0b',    // amber
  fsa: '#10b981',    // emerald
  pc: '#8b5cf6',     // violet
  lc: '#06b6d4',     // cyan
} as const;

// All boxes same width
const BW = 240;

// ─── Component ──────────────────────────────────────────────────────────────

export function ReconciliationPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const collectorQuery = useQuery({
    queryKey: ['collector-reconciliation', currentYear],
    queryFn: () => getCollectorReconciliation(currentYear),
    staleTime: 5 * 60 * 1000,
  });

  const fsa = useFSAReconciliation();
  const pcf = usePCFReconciliation();

  const isLoading = collectorQuery.isFetching || fsa.isLoading || pcf.isLoading;
  const refetch = () => {
    void collectorQuery.refetch();
    void fsa.refetch();
    void pcf.refetch();
  };

  // n=null → "—" gris (backend pendiente o sin respuesta); n>=0 → número.
  type Row = { n: number | null; l: string; c: string };

  // Collector (CC ↔ CR)
  const collectorRaw = collectorQuery.data?.data?.resumen as Record<string, unknown> | undefined;
  // Backward compat: backend viejo usa tab1, nuevo usa totals
  const collectorTotals = (collectorRaw?.totals ?? collectorRaw?.tab1) as { ok?: number; soloCC?: number; soloCR?: number; descuadre?: number } | undefined;
  const cuponRows: Row[] = [
    { n: collectorTotals?.ok ?? null, l: 'OK', c: '#22c55e' },
    { n: collectorTotals?.soloCC ?? null, l: 'Solo CC', c: '#ef4444' },
    { n: collectorTotals?.soloCR ?? null, l: 'Solo CR', c: '#f43f5e' },
    { n: collectorTotals?.descuadre ?? null, l: 'Descuadre', c: '#eab308' },
  ];

  // FSA (cobranza FSA -> FMX)
  const fsaT = fsa.resumen?.totals;
  const fsaRows: Row[] = [
    { n: fsaT?.ok ?? null, l: 'OK', c: '#22c55e' },
    { n: fsaT?.salioSinEntrar ?? null, l: 'Salio de FSA', c: '#ef4444' },
    { n: fsaT?.entroSinSalir ?? null, l: 'Llego a FMX', c: '#f43f5e' },
    { n: fsaT?.sinBanco ?? null, l: 'Sin banco', c: '#eab308' },
    { n: fsaT?.sinCuadrar ?? null, l: 'Sin cuadrar', c: '#64748b' },
  ];

  // PCF (fondeo FMX -> FSA)
  const pcfT = pcf.resumen?.totals;
  const pcfRows: Row[] = [
    { n: pcfT?.ok ?? null, l: 'OK', c: '#22c55e' },
    { n: pcfT?.salioSinEntrar ?? null, l: 'Salio de FMX', c: '#ef4444' },
    { n: pcfT?.entroSinSalir ?? null, l: 'Llego a FSA', c: '#f43f5e' },
    { n: pcfT?.sinBanco ?? null, l: 'Sin banco', c: '#eab308' },
    { n: pcfT?.sinCuadrar ?? null, l: 'Sin cuadrar', c: '#64748b' },
  ];

  // Block geometry
  const cx = 290, cy = 295;
  const fx = 560, fy = 295;
  const px = 830, py = 295;

  return (
    <div>
      <PageHeader
        title="Conciliacion"
        subtitle="Verificacion de integridad entre sistemas internos y externos"
        breadcrumbs={BREADCRUMBS}
        helpKey="reconciliation"
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Refrescar
          </Button>
        }
      />

      <div className="mt-4 rounded-xl border border-slate-800 bg-[#0b1222] p-6">
        <svg viewBox="0 0 1100 490" className="mx-auto block w-full" style={{ maxHeight: '540px' }}>

          {/* ── Arrowhead markers ───────────────────────────── */}
          <defs>
            <marker id="ah-fi" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill={CLR.fi} /></marker>
            <marker id="ah-col" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill={CLR.col} /></marker>
            <marker id="ah-fsa" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill={CLR.fsa} /></marker>
            <marker id="ah-lc" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill={CLR.lc} /></marker>
          </defs>

          {/* ── Arrows (solid) ──────────────────────────────── */}
          <line x1={260} y1={365} x2={290} y2={365} stroke={CLR.fi} strokeWidth="2" markerEnd="url(#ah-fi)" />
          <line x1={530} y1={365} x2={560} y2={365} stroke={CLR.col} strokeWidth="2" markerEnd="url(#ah-col)" />
          <line x1={680} y1={295} x2={680} y2={225} stroke={CLR.fsa} strokeWidth="2" markerEnd="url(#ah-fsa)" />
          <path d="M 800,145 C 870,145 950,200 950,300" fill="none" stroke={CLR.lc} strokeWidth="2" markerEnd="url(#ah-lc)" />

          {/* ── Fiado INC ───────────────────────────────────── */}
          <g>
            <rect x={20} y={295} width={BW} height={140} rx="8" fill="rgba(236,72,153,0.04)" stroke={CLR.fi} strokeWidth="2" />
            <text x={140} y={345} textAnchor="middle" fill={CLR.fi} fontSize="14" fontWeight="500">Fiado INC</text>
            <text x={140} y={365} textAnchor="middle" fill="#94a3b8" fontSize="10">Wallet USD</text>
          </g>

          {/* ── Fiado SA block (Transfer ↔ Banco) ── */}
          <g className="cursor-pointer" onClick={() => navigate(ROUTES.RECONCILIATION_FSA)}>
            <rect x={fx} y={fy} width={BW} height={140} rx="8" fill="none" stroke={CLR.fsa} strokeWidth="2" />
            <text x={fx + BW / 2} y={fy + 22} textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="500">Fiado SA</text>
            <text x={fx + BW / 2} y={fy + 36} textAnchor="middle" fill="#94a3b8" fontSize="9">Transfer ↔ Banco</text>
            <line x1={fx + 10} y1={fy + 44} x2={fx + BW - 10} y2={fy + 44} stroke={CLR.fsa} strokeWidth="0.5" strokeOpacity="0.5" />
            {fsaRows.map((row, ri) => {
              const ry = fy + 64 + ri * 16;
              const hasData = row.n !== null;
              const color = hasData && row.n! > 0 ? row.c : '#94a3b8';
              const valueText = hasData ? row.n!.toLocaleString() : '—';
              return (
                <g key={ri}>
                  <circle cx={fx + 20} cy={ry} r="3.5" fill={color} />
                  <text x={fx + 28} y={ry + 4} fill={color} fontSize="9">
                    {valueText} {row.l}
                  </text>
                </g>
              );
            })}
            <text x={fx + BW / 2} y={fy + 133} textAnchor="middle" fill={CLR.fsa} fontSize="7" opacity="0.6">
              Ver conciliacion →
            </text>
          </g>

          {/* ── Pago Confiado block (UNE-Credito → FSA) ── */}
          <g className="cursor-pointer" onClick={() => navigate(ROUTES.RECONCILIATION_PCF)}>
            <rect x={px} y={py} width={BW} height={140} rx="8" fill="none" stroke={CLR.pc} strokeWidth="2" />
            <text x={px + BW / 2} y={py + 22} textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="500">Pago Confiado</text>
            <text x={px + BW / 2} y={py + 36} textAnchor="middle" fill="#94a3b8" fontSize="9">UNE-Credito → FSA</text>
            <line x1={px + 10} y1={py + 44} x2={px + BW - 10} y2={py + 44} stroke={CLR.pc} strokeWidth="0.5" strokeOpacity="0.5" />
            {pcfRows.map((row, ri) => {
              const ry = py + 64 + ri * 16;
              const hasData = row.n !== null;
              const color = hasData && row.n! > 0 ? row.c : '#94a3b8';
              const valueText = hasData ? row.n!.toLocaleString() : '—';
              return (
                <g key={ri}>
                  <circle cx={px + 20} cy={ry} r="3.5" fill={color} />
                  <text x={px + 28} y={ry + 4} fill={color} fontSize="9">
                    {valueText} {row.l}
                  </text>
                </g>
              );
            })}
            <text x={px + BW / 2} y={py + 133} textAnchor="middle" fill={CLR.pc} fontSize="7" opacity="0.6">
              Ver conciliacion →
            </text>
          </g>

          {/* ── UNE-Credito ──────────────────────────────────────── */}
          <g>
            <rect x={560} y={80} width={BW} height={140} rx="8" fill="rgba(6,182,212,0.04)" stroke={CLR.lc} strokeWidth="2" />
            <text x={680} y={130} textAnchor="middle" fill={CLR.lc} fontSize="14" fontWeight="500">UNE-Credito</text>
            <text x={680} y={150} textAnchor="middle" fill="#94a3b8" fontSize="10">Fondeo y transferencia</text>
          </g>

          {/* ── Collector block (CC ↔ CR) ── */}
          <g className="cursor-pointer" onClick={() => navigate(ROUTES.RECONCILIATION_COLLECTOR)}>
            {/* Border */}
            <rect x={cx} y={cy} width={BW} height={140} rx="8" fill="none" stroke={CLR.col} strokeWidth="2" />

            {/* Title */}
            <text x={cx + BW / 2} y={cy + 22} textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="500">Collector</text>

            {/* Subtitle */}
            <text x={cx + BW / 2} y={cy + 36} textAnchor="middle" fill="#94a3b8" fontSize="9">CC ↔ CR</text>

            {/* Title separator */}
            <line x1={cx + 10} y1={cy + 44} x2={cx + BW - 10} y2={cy + 44} stroke={CLR.col} strokeWidth="0.5" strokeOpacity="0.5" />

            {/* Status rows (dots + count + label) */}
            {cuponRows.map((row, ri) => {
              const ry = cy + 64 + ri * 16;
              const hasData = row.n !== null;
              const color = hasData && row.n! > 0 ? row.c : '#94a3b8';
              const valueText = hasData ? row.n!.toLocaleString() : '—';
              return (
                <g key={ri}>
                  <circle cx={cx + 20} cy={ry} r="3.5" fill={color} />
                  <text x={cx + 28} y={ry + 4} fill={color} fontSize="9">
                    {valueText} {row.l}
                  </text>
                </g>
              );
            })}

            {/* Bottom hint */}
            <text x={cx + BW / 2} y={cy + 133} textAnchor="middle" fill={CLR.col} fontSize="7" opacity="0.6">
              Ver conciliacion →
            </text>
          </g>

        </svg>
      </div>
    </div>
  );
}
